import {Component, OnDestroy, OnInit} from '@angular/core';
import {TtsControlEnum} from '@core/enums/tts-control.enum';
import {Observable, Subject} from 'rxjs';
import {TtsHelperService} from '@shared/utils/tts-helper.service';
import {AuthRestService} from '@core/modules/rest/authentication/auth-rest.service';
import {ConsentRestService} from '@core/modules/rest/consent/consent-rest.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {getLanguageEnumByLanguageCode, LanguageEnum} from '@core/enums/language.enum';
import {OutlineChapterUnitBasicModel} from '@core/modules/rest/consent/response/outline-chapter-unit-basic.model';
import {OutlineUnitBasicModel} from '@core/modules/rest/consent/response/outline-unit-basic.model';
import {SubmitConsentRequestModel} from '@core/modules/rest/consent/request/submit-consent-request.model';
import {ConsentActions} from '../../display-consent/store';
import {TextSizeEnum} from '../../display-consent/enums/text-size.enum';
import {Store} from '@ngrx/store';
import {AppState} from '@core/reducers';
import {UnitStatusEnum} from '@core/modules/rest/consent/enums/unit-status.enum';
import {parseUnitTextForDisplaying} from '../../display-consent/helpers/unit-parsing';
import {WELCOME} from '@core/services/routes.service';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {UserSettingsModel} from '@core/modules/rest/user/response/user-settings.model';
import {filter, take, tap} from 'rxjs/operators';
import {LoggedUserDataModel} from '@core/modules/rest/user/response/logged-user-data.model';
import {CoreReducer} from '@core/store';
import * as fromCoreActions from '@core/store/core.actions';
import {LanguageStyleEnum} from '@core/enums/language-style.enum';
import {TtsRestService} from '@core/modules/rest/text-to-speech/tts-rest.service';
import {TranslateService} from '@ngx-translate/core';
import {PersonalSettingsModel} from '@shared/model/personal-settings.model';
import {PresentationModeEnum} from '@core/enums/presentation-mode.enum';
import {UserRestService} from '@core/modules/rest/user/user-rest.service';
import {ReadingContentTypeEnum} from '@core/enums/reading-content-type.enum';
import {LocationStrategy} from '@angular/common';

interface CommentsWithUnit {
    unitTitle: string;
    commentId: string;
}

@UntilDestroy()
@Component({
    selector: 'app-basic-display-consent-container',
    templateUrl: './basic-display-consent-container.component.html',
    styleUrls: ['./basic-display-consent-container.component.scss']
})
export class BasicDisplayConsentContainerComponent implements OnInit, OnDestroy {

    loggedUserInfo$: Observable<LoggedUserDataModel> = this.store.select(CoreReducer.loggedUserDataSelector);

    loading = true;
    submittedConsentId?: string;
    audio = new Audio();
    ttsUrl?: string;
    firstPlay = true;
    ttsIconState = TtsControlEnum.playState;
    pauseAudioSubject: Subject<void> = new Subject<void>();
    textToDisplay: string;
    consent: OutlineChapterUnitBasicModel[];
    unitsFlat: OutlineUnitBasicModel[];
    unitToDisplay: OutlineUnitBasicModel;
    currentUnitOrderId = 1;
    unitTitleHeader: string;
    showFinishBtn = false;
    startTime: number = Date.now();
    totalTime = 0;
    indicationId?: string;
    language: LanguageEnum;
    textSize = TextSizeEnum.SMALL;
    backButtonDisabled = true;
    loggedUserSettings: UserSettingsModel;
    personalSettings: PersonalSettingsModel;
    commentsWithUnitIds: CommentsWithUnit[] = [];
    unitMarkedWithQuestion = false;

    constructor(private ttsHelperService: TtsHelperService, private authService: AuthRestService, private consentService: ConsentRestService,
                private store: Store<AppState>, private router: Router, private ttsService: TtsRestService, private translate: TranslateService,
                private userService: UserRestService, private route: ActivatedRoute, private location: LocationStrategy) {
        history.pushState(null, '', window.location.href);
        this.location.onPopState(() => {
            if (this.currentUnitOrderId && this.currentUnitOrderId > 1) {
                history.pushState(null, '', window.location.href);
                this.onBack();
            }
        });
    }

    ngOnInit(): void {
        this.language = getLanguageEnumByLanguageCode(localStorage.getItem('languageCode'));
        this.store.dispatch(fromCoreActions.GetLoggedUserData());
        this.audio.addEventListener('ended', this.setButtonOnEnd, false);
        this.route.params.subscribe((params: Params) => {
            this.submittedConsentId = params.consentId;
            this.consentService.getBasicConsent(this.submittedConsentId).pipe(untilDestroyed(this)).subscribe((data) => {
                this.consent = data.chapterUnits;
                this.currentUnitOrderId = data.currentUnitOrderId ? data.currentUnitOrderId : 1;
                this.backButtonDisabled = this.currentUnitOrderId === 1;
                this.indicationId = this.consent[0].units[0].indicationId;
                this.populateFlatList();
                this.setUnitToDisplayByOrderId();
                this.setStartTime();
                this.loading = false;
            });

            this.consentService.getConsentComments(this.submittedConsentId).pipe(filter(value => !!value), take(1)).subscribe(comments => {
                comments.forEach(comm => {
                    this.commentsWithUnitIds.push({
                        unitTitle: comm.informationUnitTitle,
                        commentId: comm.id
                    });
                });
            });

            this.userService.getLoggedUser().pipe(filter(value => !!value), take(1)).subscribe(data => {
                this.personalSettings = JSON.parse(data.personalSettings);
                this.textSize = this.personalSettings.textSize;
            });

            this.loggedUserInfo$.pipe(tap((loggedUserData) => {
                if (loggedUserData) {
                    this.loggedUserSettings = JSON.parse(loggedUserData.settings);
                }
            })).subscribe();
        });
    }

    setButtonOnEnd = (evt: { type: string; }) => {
        if (evt.type === 'ended') {
            this.ttsIconState = TtsControlEnum.playState;
        }
    }

    readText(): void {
        if (this.firstPlay) {
            this.ttsIconState = TtsControlEnum.pauseState;
            this.convertTextToSpeech();
        } else {
            if (this.audio.ended) {
                this.play();
            } else {
                if (this.ttsIconState === TtsControlEnum.playState) {
                    this.resume(false);
                } else {
                    this.pause(false);
                }
            }
        }
    }

    onLeaveIndication(): void {
        this.authService.logout();
    }

    private convertTextToSpeech(): void {
        const removedHtmlTagsTitle = this.ttsHelperService.removeHTMLTags(this.unitToDisplay?.informationUnitTitle.replace(/(<([^>]+)>)/gi, ''));
        const removedHtmlTagsInfo = this.ttsHelperService.removeSSML(this.unitToDisplay?.informationUnitInfo);
        const finalText = removedHtmlTagsTitle.concat('. ').concat(removedHtmlTagsInfo);

        const pollyObject = {
            text: finalText,
            readingSpeed: this.ttsHelperService.getReadingSpeedByLanguageStyle(LanguageStyleEnum.EASY),
            readingLanguage: this.language,
            readingContentType: ReadingContentTypeEnum.TEXT
        };

        this.firstPlay = false;
        this.ttsService.getTTS(pollyObject).pipe(untilDestroyed(this)).subscribe((data: any) => {
            this.ttsUrl = data;
            if (this.ttsUrl) {
                this.audio.src = this.ttsUrl;
                this.play();
            }
        });
    }

    private resume(automatic: boolean): void {
        if (!this.audio.ended) {
            if (this.ttsIconState === TtsControlEnum.pauseState && automatic) {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            }
            if (this.ttsIconState === TtsControlEnum.playState && !automatic) {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            }
        }
    }

    play(): void {
        this.audio.load();
        this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
    }

    private pause(automatic: boolean): void {
        if (!automatic) {
            this.ttsIconState = TtsControlEnum.playState;
        }
        if (!this.audio.ended) {
            this.audio.pause();
        }
    }

    populateFlatList(): void {
        this.unitsFlat = [];
        this.consent?.forEach(chapter => chapter.units?.forEach(unit => {
            this.unitsFlat.push(unit);
            if (unit.children.length > 0) {
                this.getUnitChildren(unit);
            }
        }));
    }

    getUnitChildren(unit: OutlineUnitBasicModel): void {
        unit.children.forEach(item => {
            this.unitsFlat.push(item);
            if (unit.children.length > 0) {
                this.getUnitChildren(item);
            }
        });
    }

    setUnitToDisplayByOrderId(): void {
        this.unitToDisplay = this.unitsFlat.find(x => x.unitOrder === this.currentUnitOrderId);
        this.firstPlay = true;
        if (this.unitToDisplay) {
            this.unitTitleHeader = this.unitToDisplay.informationUnitTitle;
            this.textToDisplay = this.unitToDisplay.informationUnitInfo ? parseUnitTextForDisplaying(this.unitToDisplay.informationUnitInfo) : '';
            this.getCurrentUnit(this.unitToDisplay.unitOrder);
            this.showFinishBtn = this.unitToDisplay?.goAhead == null || this.unitToDisplay?.goAhead === 0;
            this.checkIfTTSShouldStartAutomatically();
            this.unitMarkedWithQuestion = this.checkIfDisplayedUnitHasComment();
        }
    }

    getCurrentUnit(unitOrderId: number): void {
        this.currentUnitOrderId = unitOrderId;
    }

    checkIfTTSShouldStartAutomatically(): void {
        if (this.personalSettings.presentationMode === PresentationModeEnum.VOICE) {
            this.convertTextToSpeech();
        }
    }

    private setStartTime(): void {
        this.startTime = Date.now();
    }

    onBack(): void {
        this.stopAudio();
        const currentUnitIndex = this.unitsFlat.indexOf(this.unitToDisplay);
        const previousUnitIndex = currentUnitIndex > 0 ? currentUnitIndex - 1 : currentUnitIndex;
        this.currentUnitOrderId = this.unitsFlat[previousUnitIndex].unitOrder;
        this.setStartTime();
        this.saveProgress();
        this.setUnitToDisplayByOrderId();
        this.backButtonDisabled = this.currentUnitOrderId === 1;
    }

    onContinue(): void {
        this.stopAudio();
        this.currentUnitOrderId = this.unitToDisplay ? this.unitToDisplay.goAhead : 1;
        this.markUnit();
        this.setStartTime();
        this.saveProgress();
        this.setUnitToDisplayByOrderId();
        this.backButtonDisabled = this.currentUnitOrderId === 1;
    }

    onComplete(): void {
        this.stopAudio();
        this.markUnit();
        const unitToShowOnReturn = this.unitToDisplay ? this.unitToDisplay.goAhead : 1;
        this.currentUnitOrderId = unitToShowOnReturn !== null ? unitToShowOnReturn : this.currentUnitOrderId;
        this.consentService.finishPatientEducation(this.submittedConsentId).subscribe( () => {
            this.saveProgress();
            this.router.navigate([WELCOME]);
        });
    }

    stopAudio(): void {
        this.pause(false);
    }

    saveProgress(): void {
        const contentWithUnitOrderId = {
            chapterUnits: this.consent,
            currentUnitOrderId: this.currentUnitOrderId,
            textSize: this.textSize
        };
        const content = JSON.stringify(contentWithUnitOrderId);

        const request: SubmitConsentRequestModel = {
            id: this.submittedConsentId,
            indicationId: this.indicationId,
            content,
            readingTime: this.totalTime,
            consentStarted: false,
            consentUpdated: true,
            languageEnum: this.language
        };

        this.store.dispatch(ConsentActions.SubmitConsent({
            request
        }));
    }

    markUnit(): void {
        const duration = this.calculateViewTime();
        this.consent = this.consent.map(chapter => {
            if (chapter.unitId === this.unitToDisplay.chapterUnitId) {
                return {
                    ...chapter,
                    units: chapter.units.map(unit => {
                        if (unit.unitOrder === this.unitToDisplay.unitOrder) {
                            return {
                                ...unit,
                                status: UnitStatusEnum.UNDERSTOOD,
                                viewDurationInMilliseconds: duration
                            };
                        } else if (unit.children.length > 0) {
                            return {
                                ...unit,
                                children: this.searchChildrenToMark(unit.children, duration)
                            };
                        }
                        return unit;
                    })
                };
            }
            return chapter;
        });
    }

    private calculateViewTime(): number {
        const endTime = Date.now();
        const timeElapsed = endTime - this.startTime;
        const prevTime = this.unitToDisplay.viewDurationInMilliseconds;

        if (prevTime > 0 && prevTime < timeElapsed) {
            this.totalTime -= prevTime;
            this.totalTime += timeElapsed;
            return timeElapsed;
        } else if (prevTime > 0 && prevTime > timeElapsed) {
            return prevTime;
        }
        this.totalTime += timeElapsed;
        return timeElapsed;
    }

    searchChildrenToMark(units: OutlineUnitBasicModel[], duration: number): OutlineUnitBasicModel[] {
        return units.map(unit => {
            if (unit.unitOrder === this.unitToDisplay.unitOrder) {
                return {
                    ...unit,
                    status: UnitStatusEnum.UNDERSTOOD,
                    viewDurationInMilliseconds: duration
                };
            } else if (unit.children.length > 0) {
                return {
                    ...unit,
                    children: this.searchChildrenToMark(unit.children, duration)
                };
            }
            return unit;
        });
    }

    onAddDeleteQuestion(): void {
        this.unitMarkedWithQuestion = !this.unitMarkedWithQuestion;
        if (this.unitToDisplay) {
            const comment = this.commentsWithUnitIds.find(com => com.unitTitle === this.unitToDisplay.informationUnitTitle);
            if (comment) {
                const index = this.commentsWithUnitIds.indexOf({
                    unitTitle: this.unitToDisplay.informationUnitTitle,
                    commentId: comment.commentId
                });
                this.commentsWithUnitIds.splice(index, 1);
                this.consentService.deleteComment(comment.commentId).subscribe(() => {
                });
            } else {
                const request = {
                    submittedConsentId: this.submittedConsentId,
                    informationUnitTitle: this.unitToDisplay.informationUnitTitle,
                    content: this.translate.instant('BASIC_MODE.QUESTION_TEXT_LARGE')
                };
                this.consentService.submitComment(request).subscribe(data => {
                    this.commentsWithUnitIds.push({
                        unitTitle: this.unitToDisplay.informationUnitTitle,
                        commentId: data.id
                    });
                });
            }
        }
    }

    checkIfDisplayedUnitHasComment(): boolean {
        return this.commentsWithUnitIds.some(info => info.unitTitle === this.unitToDisplay.informationUnitTitle);
    }

    ngOnDestroy(): void {
        this.pause(false);
    }
}
